import { LayoutGraph, NodeStackingInfo } from './types';

/**
 * Determines the stacking order (vertical order) of nodes within each level.
 *
 * For level 0: Nodes are ordered by their current Y position, then by node ID.
 * For subsequent levels: Nodes are ordered based on:
 *   1. The stacking order of nodes they receive from in the previous level
 *   2. The port order of those connections
 *   3. The receiving port names as a tiebreaker
 */
export function determineStackingOrder(graph: LayoutGraph, levels: Map<number, number>): Map<number, number> {
    const stackingOrder = new Map<number, number>();
    const nodeMap = new Map(graph.nodes.map(node => [node.id, node]));

    // Find max level
    const maxLevel = Math.max(...Array.from(levels.values()));

    // Group nodes by level
    const nodesByLevel = new Map<number, number[]>();
    for (let level = 0; level <= maxLevel; level++) {
        nodesByLevel.set(level, []);
    }

    for (const [nodeId, level] of levels.entries()) {
        nodesByLevel.get(level)!.push(nodeId);
    }

    // Process level 0: sort by Y position, then by node ID
    const level0Nodes = nodesByLevel.get(0) || [];
    const sortedLevel0 = [...level0Nodes].sort((a, b) => {
        const nodeA = nodeMap.get(a)!;
        const nodeB = nodeMap.get(b)!;

        if (nodeA.y !== nodeB.y) {
            return nodeA.y - nodeB.y;
        }
        return a - b; // fallback to node ID
    });

    sortedLevel0.forEach((nodeId, index) => {
        stackingOrder.set(nodeId, index);
    });

    // Process subsequent levels
    for (let level = 1; level <= maxLevel; level++) {
        const nodesInLevel = nodesByLevel.get(level) || [];

        // For each node, find its ordering key based on previous level connections
        const nodeOrderingKeys = new Map<number, { sendingStackIndex: number; sendingPort: string; receivingPort: string }[]>();

        for (const nodeId of nodesInLevel) {
            const incomingConnections = graph.connections.filter(
                conn => conn.receivingNodeId === nodeId && levels.get(conn.sendingNodeId) === level - 1,
            );

            const orderingKeys = incomingConnections.map(conn => ({
                sendingStackIndex: stackingOrder.get(conn.sendingNodeId) ?? 0,
                sendingPort: conn.sendingPortName,
                receivingPort: conn.receivingPortName,
            }));

            // Sort by sending node's stack index, then by port names
            orderingKeys.sort((a, b) => {
                if (a.sendingStackIndex !== b.sendingStackIndex) {
                    return a.sendingStackIndex - b.sendingStackIndex;
                }
                if (a.sendingPort !== b.sendingPort) {
                    return a.sendingPort.localeCompare(b.sendingPort);
                }
                return a.receivingPort.localeCompare(b.receivingPort);
            });

            nodeOrderingKeys.set(nodeId, orderingKeys);
        }

        // Sort nodes in this level based on their ordering keys
        const sortedNodes = [...nodesInLevel].sort((a, b) => {
            const keysA = nodeOrderingKeys.get(a) || [];
            const keysB = nodeOrderingKeys.get(b) || [];

            // If one node has no connections from previous level, it goes last
            if (keysA.length === 0 && keysB.length === 0) {
                return a - b; // fallback to node ID
            }
            if (keysA.length === 0) return 1;
            if (keysB.length === 0) return -1;

            // Compare first ordering key
            const keyA = keysA[0];
            const keyB = keysB[0];

            if (keyA.sendingStackIndex !== keyB.sendingStackIndex) {
                return keyA.sendingStackIndex - keyB.sendingStackIndex;
            }
            if (keyA.sendingPort !== keyB.sendingPort) {
                return keyA.sendingPort.localeCompare(keyB.sendingPort);
            }
            if (keyA.receivingPort !== keyB.receivingPort) {
                return keyA.receivingPort.localeCompare(keyB.receivingPort);
            }

            // Fallback to node ID
            return a - b;
        });

        sortedNodes.forEach((nodeId, index) => {
            stackingOrder.set(nodeId, index);
        });
    }

    return stackingOrder;
}
